/**
 * DateField Component - Date and datetime input field
 */

import React, { useCallback, useMemo } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { DateFieldProps } from '../../../types/forms';
import { BaseField } from './BaseField';

export const DateField: React.FC<DateFieldProps> = ({
  name,
  label,
  placeholder,
  helpText,
  required = false,
  disabled = false,
  readonly = false,
  error,
  className = '',
  value,
  min,
  max,
  format: dateFormat = 'yyyy-MM-dd',
  showTime = false,
  disabledDates,
  onChange,
  onBlur,
  onFocus,
}) => {
  const fieldId = `field-${name}`;

  // Convert value to string format for input
  const inputValue = useMemo(() => {
    if (!value) return '';

    if (value instanceof Date) {
      return format(value, showTime ? 'yyyy-MM-dd\'T\'HH:mm' : 'yyyy-MM-dd');
    }

    if (typeof value === 'string') {
      // Try to parse and reformat if needed
      try {
        const parsed = parse(value, dateFormat, new Date());
        if (isValid(parsed)) {
          return format(parsed, showTime ? 'yyyy-MM-dd\'T\'HH:mm' : 'yyyy-MM-dd');
        }
      } catch {
        // If parsing fails, return as is
      }
      return value;
    }

    return '';
  }, [value, dateFormat, showTime]);

  // Convert min/max to string format
  const minValue = useMemo(() => {
    if (!min) return undefined;
    if (min instanceof Date) {
      return format(min, 'yyyy-MM-dd');
    }
    return min;
  }, [min]);

  const maxValue = useMemo(() => {
    if (!max) return undefined;
    if (max instanceof Date) {
      return format(max, 'yyyy-MM-dd');
    }
    return max;
  }, [max]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      if (!newValue) {
        onChange?.(null);
        return;
      }

      // Parse the HTML5 date/datetime-local format
      try {
        const parsed = showTime
          ? parse(newValue, "yyyy-MM-dd'T'HH:mm", new Date())
          : parse(newValue, 'yyyy-MM-dd', new Date());

        if (isValid(parsed)) {
          // Check if date is disabled
          if (disabledDates && disabledDates(parsed)) {
            return;
          }

          // Return formatted string or Date based on what the form expects
          onChange?.(format(parsed, dateFormat));
        }
      } catch {
        // Invalid date, pass through raw value
        onChange?.(newValue);
      }
    },
    [onChange, dateFormat, showTime, disabledDates]
  );

  const inputClasses = `
    w-full px-3 py-2 pl-10
    border rounded-md shadow-sm
    focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
    ${error ? 'border-red-300' : 'border-gray-300'}
    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
    ${readonly ? 'bg-gray-50' : ''}
    transition-colors duration-200
  `;

  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      readonly={readonly}
      error={error}
      className={className}
      fieldId={fieldId}
    >
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {showTime ? (
            <Clock className="h-5 w-5 text-gray-400" />
          ) : (
            <Calendar className="h-5 w-5 text-gray-400" />
          )}
        </div>
        <input
          id={fieldId}
          name={name}
          type={showTime ? 'datetime-local' : 'date'}
          value={inputValue}
          min={minValue}
          max={maxValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readonly}
          required={required}
          className={inputClasses}
          onChange={handleChange}
          onBlur={onBlur}
          onFocus={onFocus}
          aria-invalid={!!error}
          aria-describedby={error ? `${fieldId}-error` : undefined}
        />
      </div>
    </BaseField>
  );
};

// Date range picker component
interface DateRangeFieldProps {
  name: string;
  label?: string;
  startDate: string | Date | null;
  endDate: string | Date | null;
  onStartDateChange: (date: string | Date | null) => void;
  onEndDateChange: (date: string | Date | null) => void;
  min?: string | Date;
  max?: string | Date;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helpText?: string;
}

export const DateRangeField: React.FC<DateRangeFieldProps> = ({
  name,
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  min,
  max,
  required,
  disabled,
  error,
  helpText,
}) => {
  return (
    <BaseField
      name={name}
      label={label}
      helpText={helpText}
      required={required}
      disabled={disabled}
      error={error}
    >
      <div className="flex gap-2 items-center">
        <DateField
          name={`${name}-start`}
          placeholder="Start date"
          value={startDate}
          min={min}
          max={endDate || max}
          disabled={disabled}
          onChange={onStartDateChange}
        />
        <span className="text-gray-500">to</span>
        <DateField
          name={`${name}-end`}
          placeholder="End date"
          value={endDate}
          min={startDate || min}
          max={max}
          disabled={disabled}
          onChange={onEndDateChange}
        />
      </div>
    </BaseField>
  );
};

export default DateField;